/**
 * record_conflict_controller.js — the conflict sheet for a save refused as stale.
 *
 * A save made from an out-of-date copy of a page is refused by the server (409)
 * with a conflict record (application/services/record_version_service.py
 * conflict_record). static/js/tab-session.js sees that answer and announces it
 * as the window event 'chippie:record-conflict'. This controller then:
 *
 *   1. finds the record already held in sessionStorage by tab-session.js,
 *      keyed by the record ("quote:530"), so it survives the reload and a
 *      conflict from an autosave on leaving a page waits for the return;
 *   2. reloads the page, when the conflict is for the record on screen. A full reload, not a Turbo visit: the service worker
 *      serves Turbo visits cache-first, but browser navigations network-first,
 *      so the reload is the record as it is now;
 *   3. on connect, finds a held conflict for the record this page shows and
 *      opens the sheet: per field, what the record says now beside what the
 *      user typed.
 *
 * The user's input is never discarded without their say: "Save mine anyway"
 * resends the original save against the new version (a deliberate
 * overwrite), "Copy my text" puts their values on the clipboard to merge by
 * hand, "Discard mine" drops the held conflict.
 *
 * Renders from the conflict record only, so an autosave on leaving a page and
 * offline edits syncing reuse this sheet unchanged. Markup:
 * templates/macros/record_conflict.html.
 */

import { Controller } from "@hotwired/stimulus";

/** The held-conflict key for one record; tab-session.js owns its format. */
function recordKey(type, id) {
    return window.chippieSession.conflictKey(type, id);
}

function shownRecordKey() {
    const version = document.body && document.body.dataset.recordVersion;
    if (!version) return null;
    const [type, id] = version.split(":");
    return recordKey(type, id);
}

function readHeld(key) {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
}

function writeHeld(key, record) {
    try {
        if (record) sessionStorage.setItem(key, JSON.stringify(record));
        else sessionStorage.removeItem(key);
    } catch (_e) { /* storage unavailable: the sheet cannot survive a reload */ }
}

/** The resend body as fetch sends it: FormData for a form save, JSON otherwise. */
function resendBody(resend) {
    if (resend.encoding === "json") return JSON.stringify(resend.body);
    const form = new FormData();
    Object.entries(resend.body || {}).forEach(([name, values]) => {
        [].concat(values).forEach((value) => form.append(name, value));
    });
    return form;
}

const EMPTY = "(empty)";
// Past this many token pairs the comparison is not worth the time on a phone;
// both values still show in full, just without the marked words.
const DIFF_LIMIT = 250000;

function asText(value) {
    return value == null || value === "" ? EMPTY : String(value);
}

/**
 * Split two texts into runs, marking the words each has that the other lacks
 * (longest common subsequence over words, whitespace kept). Returns
 * [nowRuns, yoursRuns], each a list of { text, changed }.
 */
function wordDiff(now, yours) {
    const a = now.split(/(\s+)/);
    const b = yours.split(/(\s+)/);
    if (a.length * b.length > DIFF_LIMIT) {
        return [[{ text: now, changed: false }], [{ text: yours, changed: false }]];
    }
    const lcs = Array.from({ length: a.length + 1 }, () => new Uint16Array(b.length + 1));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }
    const nowRuns = [];
    const yoursRuns = [];
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) {
            nowRuns.push({ text: a[i++], changed: false });
            yoursRuns.push({ text: b[j++], changed: false });
        } else if (j < b.length && (i >= a.length || lcs[i][j + 1] >= lcs[i + 1][j])) {
            yoursRuns.push({ text: b[j], changed: /\S/.test(b[j++]) });
        } else {
            nowRuns.push({ text: a[i], changed: /\S/.test(a[i++]) });
        }
    }
    return [nowRuns, yoursRuns];
}

/** One labelled value box, its differing words wrapped in <del> or <ins>. */
function valueBox(caption, runs, markTag, modifier) {
    const box = document.createElement("div");
    box.className = `record-conflict-value${modifier ? ` ${modifier}` : ""}`;
    const label = document.createElement("span");
    label.className = "record-conflict-value-caption";
    label.textContent = caption;
    box.append(label);
    runs.forEach((run) => {
        if (!run.changed) {
            box.append(document.createTextNode(run.text));
            return;
        }
        const mark = document.createElement(markTag);
        mark.textContent = run.text;
        box.append(mark);
    });
    return box;
}

function notify(message, type) {
    if (window.showNotification) window.showNotification(message, type);
}

export default class extends Controller {
    static targets = ["title", "fields"];

    connect() {
        this._onConflict = (event) => this.hold(event.detail);
        window.addEventListener("chippie:record-conflict", this._onConflict);
        const key = shownRecordKey();
        const record = key && readHeld(key);
        if (record) this.show(record);
    }

    disconnect() {
        window.removeEventListener("chippie:record-conflict", this._onConflict);
    }

    /** tab-session.js has already held the record. Reload only when it is the
     *  record on screen; a conflict for a page the user has left (an autosave
     *  fired while navigating away) waits until they return to that record. */
    hold(record) {
        if (recordKey(record.entity.type, record.entity.id) === shownRecordKey()) {
            window.location.reload();
        }
    }

    show(record) {
        this.record = record;
        this.titleTarget.textContent = `${record.entity.label} changed since you opened it.`;
        this.fieldsTarget.replaceChildren(...record.fields.map((field) => this.fieldRow(field)));
        bootstrap.Modal.getOrCreateInstance(this.element).show();
    }

    fieldRow(field) {
        const row = document.createElement("div");
        row.className = "record-conflict-field";
        const label = document.createElement("div");
        label.className = "record-conflict-field-label";
        label.textContent = field.label;
        const [now, yours] = wordDiff(asText(field.now), asText(field.yours));
        row.append(label, valueBox("Now", now, "del"), valueBox("Yours", yours, "ins", "record-conflict-value--yours"));
        return row;
    }

    async resend() {
        const { resend, entity } = this.record;
        const headers = { "X-Record-Version": document.body.dataset.recordVersion || entity.current_version };
        if (resend.encoding === "json") headers["Content-Type"] = "application/json";
        const response = await fetch(resend.url, {
            method: resend.method, body: resendBody(resend), headers, credentials: "same-origin",
        });
        // A second conflict is announced by tab-session.js and replaces this one.
        if (response.status === 409) return;
        if (!response.ok) {
            notify("Your change could not be saved. Try again, or copy your text.", "error");
            return;
        }
        this.clear();
        window.location.reload();
    }

    async copy() {
        const text = this.record.fields.map((field) => `${field.label}: ${field.yours ?? ""}`).join("\n\n");
        try {
            await navigator.clipboard.writeText(text);
            notify("Your text is copied.", "success");
        } catch (_e) {
            notify("Could not copy. Select the text in the sheet instead.", "warning");
        }
    }

    discard() {
        this.clear();
        bootstrap.Modal.getOrCreateInstance(this.element).hide();
    }

    clear() {
        writeHeld(recordKey(this.record.entity.type, this.record.entity.id), null);
    }
}
